// Vue « Sites web » (#/websites, fiche : #/websites/{id}) : sites et services
// HTTP(S) sondés par la plateforme elle-même, à intervalle fixe.
//
// Un site n'est pas un appareil : il a sa propre liste, et n'apparaît dans le
// parc que par le serveur auquel il est éventuellement rattaché (ses alertes y
// atterrissent alors).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Globe, Plus, Search, Pause, Play } from 'lucide-react';
import { api } from '../lib/api.jsx';
import { useDashboard } from './dashboardContext.jsx';
import { useTableSort } from '../lib/useTableSort.jsx';
import { parseHash, viewToHash } from '../lib/router.jsx';
import { fmtRelativeFromNow } from '../lib/format.jsx';
import { ClientPicker } from '../components/ClientPicker.jsx';
import { WebMonitorModal } from '../components/WebMonitorModal.jsx';
import { WebMonitorDetail } from '../components/WebMonitorDetail.jsx';
import {
  wmCall,
  monitorState,
  StatusDot,
  UptimeBars,
  fmtPct,
  pctTone,
  fmtInterval,
  certDaysLeft,
  certTone,
  stateSinceText,
} from '../lib/webmonitor.jsx';

const inputS = 'bg-card border border-rule-2 rounded-lg px-2.5 py-1.5 text-fg-2 text-xs outline-none';
const btnF = (active) =>
  `rounded-lg px-3 py-1 text-[11px] cursor-pointer border transition-colors ${
    active
      ? 'bg-blue-500/15 border-blue-500 text-blue-400 font-semibold hover:bg-blue-500/25'
      : 'bg-card border-rule-2 text-muted hover:bg-card-hi hover:text-fg-2 hover:border-rule-3'
  }`;
const thCls = 'px-3 py-2 text-left text-muted-2 font-medium text-[11px] uppercase border-b border-rule whitespace-nowrap';
const tdCls = 'px-3 py-2.5 text-fg-2 border-b border-sidebar text-[12px]';

const STATUS_FILTERS = [
  ['', 'Tous'],
  ['down', 'En panne'],
  ['up', 'En service'],
  ['paused', 'En pause'],
];
const matchesStatus = (m, f) => {
  const k = monitorState(m).key;
  if (f === 'down') return k === 'down' || k === 'confirming';
  if (f === 'up') return k === 'up' || k === 'pending';
  if (f === 'paused') return k === 'paused';
  return true;
};
// Ordre « par état » : ce qui demande une action d'abord.
const STATE_ORDER = { down: 0, confirming: 1, pending: 2, up: 3, paused: 4 };

/** Hôte + chemin, sans le schéma : c'est ce qu'on lit d'un coup d'œil. */
const shortUrl = (u) => (u || '').replace(/^https?:\/\//i, '');

export function WebMonitorsPage() {
  const [param, setParam] = useState(() => parseHash().param);
  useEffect(() => {
    const onHash = () => setParam(parseHash().param);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  if (param) return <WebMonitorDetail id={param} />;
  return <WebMonitorList />;
}

function WebMonitorList() {
  const { can } = useDashboard();
  const canManage = can('ManageProvisioning');
  const canFilterByClient = can('ViewClients');
  const urlQ = parseHash().query;
  const [filters, setFilters] = useState({
    clientId: urlQ.clientId ?? null,
    search: urlQ.search ?? '',
    status: STATUS_FILTERS.some(([k]) => k === urlQ.status) ? urlQ.status ?? '' : '',
  });
  const [rows, setRows] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(null);
  const sortCtl = useTableSort('state');
  const { sortRows, headerProps, arrow } = sortCtl;

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (filters.clientId) p.set('clientId', filters.clientId);
    if (filters.search.trim()) p.set('search', filters.search.trim());
    const qs = p.toString();
    const r = await api.get('/platform/web-monitors' + (qs ? '?' + qs : ''));
    setRows(r || []);
  }, [filters.clientId, filters.search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => {
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    const hash = viewToHash('websites', null, null, {
      clientId: filters.clientId,
      search: filters.search,
      status: filters.status,
    });
    if (window.location.hash !== hash) window.history.replaceState(null, '', hash);
  }, [filters]);

  const counts = useMemo(() => {
    const c = { '': 0, down: 0, up: 0, paused: 0 };
    for (const m of rows || []) {
      c['']++;
      for (const [k] of STATUS_FILTERS.slice(1)) if (matchesStatus(m, k)) c[k]++;
    }
    return c;
  }, [rows]);

  const visible = (rows || []).filter((m) => matchesStatus(m, filters.status));
  const sorted = sortRows(visible, (m, key) => {
    switch (key) {
      case 'state':
        return STATE_ORDER[monitorState(m).key] * 1e14 + (m.statusSince ? new Date(m.statusSince).getTime() : 0);
      case 'name':
        return m.name || '';
      case 'client':
        return m.clientName || '';
      case 'uptime':
        return m.uptime30d;
      case 'ms':
        return m.lastResponseMs;
      case 'cert':
        return certDaysLeft(m);
      case 'checked':
        return m.lastCheckAt ? new Date(m.lastCheckAt).getTime() : null;
      default:
        return null;
    }
  });

  const setF = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const open = (m) => (window.location.hash = '#/websites/' + m.id);
  const togglePause = async (e, m) => {
    e.stopPropagation();
    setBusy(m.id);
    await wmCall('/' + m.id + (m.enabled ? '/pause' : '/resume'), 'POST');
    setBusy(null);
    load();
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <h1 className="text-[22px] font-bold text-fg inline-flex items-center gap-2">
          <Globe size={22} strokeWidth={1.75} /> Sites web
          {rows && <span className="text-sm font-normal text-muted-2">({visible.length})</span>}
        </h1>
        {canManage && (
          <button
            onClick={() => setShowAdd(true)}
            className="bg-blue-500 border-none rounded-lg px-4 py-2 text-white cursor-pointer text-xs font-semibold hover:bg-blue-600 inline-flex items-center gap-1.5"
          >
            <Plus size={14} strokeWidth={2} /> Surveiller un site
          </button>
        )}
      </div>

      <div className="flex items-center gap-2.5 mb-4 flex-wrap">
        <div className="relative">
          <Search size={13} strokeWidth={1.75} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-2" />
          <input
            className={`${inputS} pl-7 w-[230px]`}
            placeholder="Nom ou adresse…"
            value={filters.search}
            onChange={(e) => setF({ search: e.target.value })}
          />
        </div>
        {canFilterByClient && (
          <ClientPicker
            className="w-[240px]"
            value={filters.clientId}
            onChange={(id) => setF({ clientId: id || null })}
            placeholder="Tous les clients"
          />
        )}
        <div className="flex gap-1.5">
          {STATUS_FILTERS.map(([k, l]) => (
            <button key={k} className={btnF(filters.status === k)} onClick={() => setF({ status: k })}>
              {l}
              {rows && (
                <span className={`ml-1.5 font-mono ${k === 'down' && counts.down > 0 ? 'text-crit' : ''}`}>{counts[k]}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-card border border-rule rounded-2xl overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={thCls} {...headerProps('state')}>
                État{arrow('state')}
              </th>
              <th className={thCls} {...headerProps('name')}>
                Site{arrow('name')}
              </th>
              <th className={thCls} {...headerProps('client')}>
                Client{arrow('client')}
              </th>
              <th className={thCls}>24 dernières heures</th>
              <th className={thCls} {...headerProps('uptime')}>
                30 jours{arrow('uptime')}
              </th>
              <th className={thCls} {...headerProps('ms')}>
                Réponse{arrow('ms')}
              </th>
              <th className={thCls} {...headerProps('cert')}>
                Certificat{arrow('cert')}
              </th>
              <th className={thCls} {...headerProps('checked')}>
                Vérifié{arrow('checked')}
              </th>
              {canManage && <th className={`${thCls} sticky right-0 bg-card`}></th>}
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr>
                <td colSpan={9} className="p-8 text-center text-muted-2 text-xs">
                  Chargement…
                </td>
              </tr>
            )}
            {rows && sorted.length === 0 && (
              <tr>
                <td colSpan={9} className="p-8 text-center text-muted-2 text-xs">
                  {rows.length === 0 && !filters.clientId && !filters.search
                    ? 'Aucun site surveillé pour le moment.'
                    : 'Aucun site ne correspond aux filtres.'}
                </td>
              </tr>
            )}
            {sorted.map((m) => {
              const s = monitorState(m);
              const days = certDaysLeft(m);
              return (
                <tr key={m.id} className="hover:bg-card-hi cursor-pointer" onClick={() => open(m)}>
                  <td className={`${tdCls} whitespace-nowrap`}>
                    <span className="inline-flex items-center gap-2">
                      <StatusDot m={m} />
                      <span className={s.tone}>
                        {s.label}
                        {s.since && <span className="text-muted-2"> · {fmtRelativeFromNow(s.since)}</span>}
                      </span>
                    </span>
                    {s.key === 'down' && m.lastError && (
                      <div className="text-[10.5px] text-crit/90 mt-0.5 max-w-[220px] truncate" title={m.lastError}>
                        {m.lastError}
                      </div>
                    )}
                  </td>
                  <td className={tdCls}>
                    <a href={'#/websites/' + m.id} onClick={(e) => e.stopPropagation()} className="text-blue-400 hover:underline font-semibold">
                      {m.name}
                    </a>
                    <div className="text-[10.5px] text-muted-2 font-mono max-w-[320px] truncate" title={m.url}>
                      {m.method !== 'GET' && <span className="text-muted font-semibold mr-1">{m.method}</span>}
                      {shortUrl(m.url)}
                    </div>
                  </td>
                  <td className={tdCls}>
                    {m.clientName || <span className="text-muted-2">Interne</span>}
                    {m.agentName && <div className="text-[10px] text-muted-2">{m.agentName}</div>}
                  </td>
                  <td className={tdCls}>
                    <UptimeBars hours={m.last24h} />
                  </td>
                  <td className={`${tdCls} font-mono whitespace-nowrap ${pctTone(m.uptime30d)}`}>{fmtPct(m.uptime30d)}</td>
                  <td className={`${tdCls} font-mono whitespace-nowrap`}>
                    {m.lastResponseMs != null && s.key !== 'paused' ? m.lastResponseMs + ' ms' : <span className="text-muted-2">—</span>}
                  </td>
                  <td className={`${tdCls} whitespace-nowrap ${certTone(m)}`}>
                    {days == null ? <span className="text-muted-2">—</span> : days < 0 ? 'Expiré' : days + ' j'}
                  </td>
                  <td className={`${tdCls} whitespace-nowrap`} title={stateSinceText(m)}>
                    {m.lastCheckAt ? 'il y a ' + fmtRelativeFromNow(m.lastCheckAt) : <span className="text-muted-2">jamais</span>}
                    <div className="text-[10px] text-muted-2">toutes les {fmtInterval(m.intervalSeconds)}</div>
                  </td>
                  {canManage && (
                    <td className={`${tdCls} text-right sticky right-0 bg-card`}>
                      <button
                        onClick={(e) => togglePause(e, m)}
                        disabled={busy === m.id}
                        title={m.enabled ? 'Mettre en pause' : 'Reprendre la surveillance'}
                        className="bg-rule border border-rule-3 rounded-md px-2 py-1 text-fg-2 cursor-pointer hover:bg-rule-2 disabled:opacity-50 inline-flex items-center"
                      >
                        {m.enabled ? <Pause size={13} /> : <Play size={13} />}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <WebMonitorModal
          monitor={null}
          onClose={(saved) => {
            setShowAdd(false);
            if (saved) load();
          }}
        />
      )}
    </div>
  );
}
